import type { ReactNode } from 'react'

import { Notice, Pill, RESEARCH_NOTES_ID } from '../ui'
import { PageEnd } from '../PageEnd'
import { BrainScan } from '../brain'
import { presentWarnings } from '../../lib/warnings'
import { researchBlocks, researchRows, shownValidityFlags, uiCardUnavailable } from '../../lib/dailyStatus'
import type {
  DailyStatusView as DailyStatus,
  DailyValidity,
  ScoreCard as ScoreCardData,
  UiScores,
} from '../../lib/dailyStatus'
import { BREAKPOINTS } from '../../lib/breakpoints'
import { formatConfidence } from '../../lib/format'
import { PRO_DEVICE_ID } from '../../lib/devices'
import { useMediaQuery } from '../../lib/useMediaQuery'
import { DailyHeadline } from './DailyHeadline'
import { RECORDING_SLOT, ringSet } from './ringSet'
import { ScoreCard, type ScoreCardRecording } from './ScoreCard'

/**
 * A whole Daily Status — on Today, on a past date's detail view, and on the
 * result Record shows when a job finishes.
 *
 * ── NOTHING HERE SAYS "TODAY" ANY MORE ──────────────────────────────────────
 *
 * Three strings did, until Stage 6a gave this component its second host. Read on
 * a recording from three weeks ago, "treat today as a trend signal" and
 * "recording again today sharpens nothing" are about a day the reader is not
 * looking at — the second is actively wrong advice, since the baseline day they
 * would be adding to is that recording's, not this morning's. They are date-
 * neutral now rather than branched on an `isToday` prop: a branch is a second
 * rendering nobody tests, and the neutral wording is true on both screens.
 *
 * ── ONE ORDER, ON ALL THREE HOSTS (O5) ──────────────────────────────────────
 *
 *   headline · confidence · About this recording · hero · measure rows ·
 *   `afterRows` · research · disclaimer
 *
 * The order is a decision that must not be made twice, which is why it is
 * here and not in each host. What each part is doing there:
 *
 *   - **The headline is INSIDE the body**, not in the page header, so all
 *     three hosts render the payload's `summary`. Putting it in Today's
 *     header would have given Record and RecordingDetail a body with no
 *     sentence at the top of it.
 *   - **Signal quality is rendered once and first among the numbers** (R13).
 *     It is a property of the recording rather than one of the things
 *     measured in it, and it gates the rest: a reader who sees the six before
 *     they know whether the signal was clean has read them in the wrong
 *     order. So it is the recording card, beside the montage slot, and it is
 *     neither a ring nor a row.
 *   - **A ring and a row are one slot drawn twice** — the dial for the shape
 *     of the day, the row for the number, the description, the reason and the
 *     precaution. `ringSet` is the single definition of which six those are,
 *     and the data layer is untouched: `SCORE_SLOTS` is still eight and
 *     `status.scores` is still length eight.
 *   - **`afterRows` is a HOST SLOT and nothing more.** It is rendered as a
 *     plain sibling between the rows and the research section, so it can
 *     never sit above a score and can never become a guard on the disclaimer.
 *     Today passes its journal summary; Record and RecordingDetail pass
 *     nothing.
 *
 * The disclaimer is last and unguarded, on every host, always.
 */

/**
 * §1.2's "prominent, explanatory state", as a card rather than a notice.
 *
 * ── WHY IT IS NOT A `Notice` ANY MORE ───────────────────────────────────────
 *
 * It was one, and a `notice--warn` puts an amber wash under an amber pill: in
 * the light theme that measures 4.25:1 for the pill's own label, under the 4.5
 * an 11px label needs. The card ground (`--grad-card`) is what keeps the amber
 * pill legal in light, where low confidence uses the border and two pills
 * without a wash (R16). Dark uses its separately contrast-tested amber wash.
 * Amber, never red — a held-low
 * confidence is a statement about the baseline's length, not a fault.
 *
 * The baseline countdown moved INSIDE it: the two were separate boxes saying
 * one thing, and the reason confidence is held low is usually the baseline.
 *
 * Every sentence below is unchanged, and the number is the payload's own,
 * through the one confidence formatter every screen uses (UI-QA Q8: at most one
 * decimal place) — never a percentage, never a grade. `0.0` renders as
 * `Confidence 0`, which is what the payload said.
 */
export function ConfidenceBanner({ status }: { status: DailyStatus }) {
  const { confidence, lowConfidence } = status
  return (
    <section
      className={`confidence${lowConfidence ? ' confidence--low' : ''}`}
      aria-label="Confidence"
    >
      <div className="confidence__text">
        <div className="confidence__title">
          {/* The payload's own number, as a number. Not a band, not a grade,
              not a percentage of anything -- the client has no scale to
              convert it to. `!== null`, because 0 is a confidence. */}
          {formatConfidence(confidence) !== null && (
            <Pill tone={lowConfidence ? 'amber' : 'green'} dot>
              Confidence {formatConfidence(confidence)}
            </Pill>
          )}
          {lowConfidence && <Pill tone="amber">Held low</Pill>}
        </div>
        <p className="confidence__headline">
          {lowConfidence
            ? 'Treat this as a trend signal rather than a reading.'
            : 'Reliable enough to show as a reading.'}
        </p>
        {lowConfidence && (
          <p className="confidence__why">
            Confidence is held low while your baseline is short, and when a recording is
            brief, noisy or unsteady.
          </p>
        )}
      </div>
      <BaselineProgress status={status} />
    </section>
  )
}

/**
 * The baseline's own words, and the countdown while it is still provisional.
 *
 * It renders whenever there IS a baseline status, not only while provisional:
 * `display` is a verbatim payload string on a confirmed baseline too, and
 * hiding it meant the one line that says where the baseline stands disappeared
 * at the moment it became good news.
 */
export function BaselineProgress({ status }: { status: DailyStatus }) {
  const baseline = status.baselineStatus
  if (baseline === null) return null
  const remaining = baseline.days_confirmed_remaining
  return (
    <section className="baseline-progress" aria-label="Baseline progress">
      {/* Verbatim. §1.3 -- this string is built by the package for exactly this
          place and paraphrasing it would put a client-authored sentence where a
          governed one belongs. */}
      <p className="baseline-progress__display">{baseline.display}</p>
      {/* ── A COUNTDOWN, NOT A BAR ──────────────────────────────────────────
          The payload carries `days_confirmed_remaining` and nothing else. A
          `<progress>` needs a denominator, and the only one available is
          `tracking_min_baseline_sessions` — a backend configuration value the
          client would have to hard-code, which is the client-side threshold
          acceptance criterion 2 forbids.

          The bar that stood here used `max={remaining} value={0}`, so a user one
          day into a three-day baseline saw an empty bar over the number 2: zero
          progress out of the days still to go, which is false twice and looks
          authoritative. The number is the fact; the package's own display string
          above already phrases it as progress. */}
      {baseline.provisional && (
        <>
          <p className="baseline-progress__count">
            <span className="baseline-progress__days">{remaining}</span>{' '}
            {remaining === 1 ? 'more separate day' : 'more separate days'} to a confirmed
            baseline
          </p>
          <p className="baseline-progress__note">
            Only separate days count. A second recording on the same day sharpens nothing —
            the baseline advances on distinct days, not on sessions.
          </p>
        </>
      )}
    </section>
  )
}

/**
 * Everything the payload says about validity, attributed to nothing.
 *
 * `validity` is a set of booleans with no slot names in it. Two of them the
 * package itself pairs with a score and a token, and those are rendered on the
 * card. The rest are shown here, adjacent to the grid rather than under it, and
 * pointed at no particular number — because deciding which number they explain
 * is the inference acceptance criterion 2 forbids.
 */
export function ValidityNote({ status }: { status: DailyStatus }) {
  const consumed = new Set(status.scores.flatMap((card) => card.unavailableReasons))
  const { shown, staleAnalysis } = presentWarnings(
    status.activeWarnings.filter((code) => !consumed.has(code)),
  )
  const reasons = status.validity?.reasons ?? []
  const reactivity = status.validity?.alpha_reactivity ?? null
  /**
   * The three per-band booleans, which were parsed and rendered nowhere.
   *
   * Rendered as `key: true|false` text, in this order, and NOT as a ✓ or a ✕:
   * a glyph is a verdict, and "valid" here is the package's own word for a
   * band that met its gate, not a grade on the recording (R17). No count
   * either — it would cost a permanent allowlist entry and say nothing the
   * list does not.
   *
   * `typeof === 'boolean'` rather than a truthiness test, because `false` is
   * the answer that matters. A flag that gates nothing this headband
   * produced is not shown at all: those measurements render as their payload
   * sentences instead (`shownValidityFlags`, `ResearchSection`).
   */
  const flags = shownValidityFlags(status)

  if (
    shown.length === 0
    && reasons.length === 0
    && reactivity === null
    && flags.length === 0
    && !staleAnalysis
  ) {
    return null
  }

  /**
   * UI-QA Q6: the MAIN READING holds sentences only. A code is explained by
   * the payload's own sentence (`validity.reason_text`, the warnings' copy)
   * where one exists; where none exists the line says so neutrally and the code
   * itself sits in the labelled "Technical details" disclosure, never guessed
   * into words. The per-band flags are diagnostics: they are listed there too,
   * and a `false` among them keeps a plain line in the main reading, so a failed
   * check is never hidden behind a click.
   */
  const reasonText = status.validity?.reason_text ?? {}
  const explained = reasons.filter((reason) => reasonText[reason] !== undefined)
  const unexplained = reasons.filter((reason) => reasonText[reason] === undefined)
  const uncopied = shown.filter(({ copy }) => copy == null).map(({ code }) => code)
  const failedCheck = flags.some(([, value]) => value === false)
  const technical = uncopied.length !== 0 || unexplained.length !== 0 || reactivity !== null || flags.length !== 0

  return (
    <section className="validity-note card" aria-label="About this recording">
      <div className="card__head">
        <div className="card__eyebrow">About this recording</div>
      </div>
      {staleAnalysis && (
        <Notice tone="warn">
          This result was produced by an older version of the analysis. It is shown as it
          was recorded and has not been re-scored.
        </Notice>
      )}
      <ul className="validity-note__list" role="list">
        {shown.filter(({ copy }) => copy != null).map(({ code, copy }) => (
          <li key={code}>{copy}</li>
        ))}
        {explained.map((reason) => (
          <li key={reason}>{reasonText[reason]}</li>
        ))}
        {reactivity !== null && (
          <li>
            {reactivity === 'requires_paired_session'
              ? 'Eyes-open/eyes-closed reactivity was not calculated for this recording.'
              : 'Eyes-open/eyes-closed reactivity has a note under Technical details.'}
          </li>
        )}
        {(uncopied.length !== 0 || unexplained.length !== 0) && (
          <li>This recording carries a code this screen has no description for. It is listed under Technical details.</li>
        )}
        {failedCheck && <li>Not every validity check passed for this recording. The checks are listed under Technical details.</li>}
      </ul>
      {technical && (
        <details className="tech-details">
          <summary>Technical details</summary>
          {/* The raw codes and the flags, as data rather than as sentences:
              they are the payload's own spelling and they are not prose. */}
          <ul className="warnings">
            {uncopied.map((code) => (
              <li key={`w:${code}`}>
                <code className="tag">{code}</code>
              </li>
            ))}
            {reactivity !== null && (
              <li>
                <code className="tag">alpha_reactivity: {reactivity}</code>
              </li>
            )}
            {unexplained.map((reason) => (
              <li key={`r:${reason}`}>
                <code className="tag">{reason}</code>
              </li>
            ))}
            {flags.map(([key, value]) => (
              <li key={key}>
                <code className="tag">
                  {key}: {String(value)}
                </code>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}

/** Rendered, never paraphrased. §1.5, and criterion 9 on every screen with a score. */
export function Disclaimer({ status }: { status: DailyStatus }) {
  if (status.disclaimer === null) return null
  return (
    <aside className="disclaimer" aria-label="Disclaimer">
      {status.disclaimer}
    </aside>
  )
}

/** A research block the payload itself marks: `badge: "research_only"` or `validated: false`. */
function researchMarked(block: { entries: ReadonlyArray<readonly [string, unknown]> }): boolean {
  return block.entries.some(([key, value]) => (key === 'badge' && value === 'research_only') || (key === 'validated' && value === false))
}

/**
 * The research notes, at the END of the page (R73), and the stored blocks
 * behind the readout.
 *
 * ── WHAT IT SAYS (UI-QA Q7) ─────────────────────────────────────────────────
 *
 * The note used to say these measures were "off by default", "shown because a
 * research flag is set on the account" and "not part of the daily readout",
 * while the same page led with the four `ui_scores` cards and listed
 * `ui_scores` again in this section. None of that was established here. It now
 * says only what the payload marks and where the screen puts it: the research-
 * only measures (`RESEARCH_ONLY_SLOTS`) and the blocks whose own `badge` or
 * `validated` mark them. Blocks the payload does not mark (`ui_scores`, which
 * leads Report under R59, and `advanced`) are not called research: they stay,
 * as stored, under "Technical details". Each measure keeps its own limits.
 *
 * A research slot with NO VALUE and no reason is simply absent; a slot named in
 * `validity.unavailable` renders as its sentence (Sprint 1.9.2 S4). The grid is
 * conditional, so a caption never sits over an empty box.
 *
 * Rendered through `PageEnd`, so inside the shell it follows everything the
 * page shows, including a host's content after `DailyStatusBody`.
 */
export function ResearchSection({ status }: { status: DailyStatus }) {
  const researchCards = status.scores.filter(
    (card) => card.researchOnly && (card.value !== null || card.unavailable !== null),
  )
  const blocks = researchBlocks(status)
  const marked = blocks.filter(researchMarked)
  const stored = blocks.filter((block) => !researchMarked(block))
  const research = researchCards.length !== 0 || marked.length !== 0
  if (!research && stored.length === 0) return null

  const blockDetails = (block: (typeof blocks)[number]) => {
    const rows = researchRows(block, status.validity)
    return (
      <details className="research-block" key={block.key}>
        <summary>
          <span className="mono-cell">{block.key}</span> ({rows.length})
        </summary>
        <dl className="research-block__list">
          {rows.map((row) => (
            <div className="research-block__row" key={row.field}>
              <dt>{row.field}</dt>
              {/* An unavailable measurement is its reason: the payload's
                  sentence, or its token as a tag when it sent no sentence --
                  never `null` (`researchRows`). Anything else is rendered as
                  it arrived. */}
              <dd>
                {row.kind === 'unavailable'
                  ? (row.reason.sentence ?? <code className="tag">{row.reason.token}</code>)
                  : typeof row.value === 'object'
                    ? JSON.stringify(row.value)
                    : String(row.value)}
              </dd>
            </div>
          ))}
        </dl>
      </details>
    )
  }

  return (
    <>
      {research && (
        <section
          id={RESEARCH_NOTES_ID}
          className="research-section research-notes"
          aria-label="Research-only measures"
          tabIndex={-1}
        >
          <h2 className="research-section__title research-notes__title">
            <span className="research-marker research-marker--legend" aria-hidden="true">R</span> Research only
          </h2>
          <p className="research-section__note research-notes__note">
            This result marks the measures in this section as research only. They are kept here, at the
            end of the page, apart from the readout above, and each one’s own limits are stated with it.
          </p>
          {researchCards.length > 0 && (
            <div className="score-grid">
              {researchCards.map((card) => (
                <ScoreCard key={card.slot} card={card} />
              ))}
            </div>
          )}
        </section>
      )}
      {(stored.length !== 0 || marked.length !== 0) && (
        <details className="tech-details tech-details--stored" aria-label="Stored result blocks">
          <summary>Technical details</summary>
          {marked.map(blockDetails)}
          {stored.map(blockDetails)}
        </details>
      )}
    </>
  )
}

/** The hero's own breakpoint, and NOT the shell's `lg`. See `Montage`. */
const DESKTOP = `(min-width: ${BREAKPOINTS.xl}px)`

/** The design's two sizes: canvas 413/974 above, canvas 522 below. */
const MONTAGE = {
  below: { size: 200, maxPoints: 1600, showLabels: false },
  desktop: { size: 320, maxPoints: 3200, showLabels: true },
} as const

/**
 * The BrainScan montage, at whichever of its two sizes the slot is.
 *
 * ── IT IS DECORATION, AND NOTHING FEEDS IT ──────────────────────────────────
 *
 * No score, no slot, no confidence and no part of the payload reaches it: it
 * draws the BrainBit Pro montage, which is the same eight electrodes on every
 * Pro recording anyone will ever open (design build note 1376). So it takes no
 * props from `status`, and no reading can be inferred from what it shows. The
 * one thing that decides it is the row's headband: it is mounted on a Pro day
 * only (`BrainSlot`).
 *
 * ── ONE ELEMENT, TWO PROP SETS ──────────────────────────────────────────────
 *
 * The switch is a ternary over the props of a single `<BrainScan>`, never two
 * elements toggled by CSS: a second element is a second rendering, and only one
 * of them would ever be the one a test looked at. Same reason there is no
 * `showBrain` prop and no per-host variant — all three hosts of
 * `DailyStatusBody` (Today, Recording detail, Record's scored result) get the
 * same body (O5, and the header note above: "a branch is a second rendering
 * nobody tests").
 *
 * ── WHY `xl` AND NOT `lg` ───────────────────────────────────────────────────
 *
 * `product.css`'s *hero* region sizes `.brain-slot` 200px and grows it to 240px
 * from 1280px — the design's `.today__hero` container query
 * (`docs/design/product-v3/web-styles/_harness/app.css:1643`, a frozen
 * reference copy). The canvas must switch on the SAME condition as its box, or
 * a 240px canvas is drawn into a 200px slot for the whole 1024–1279 band. The
 * app's other breakpoint read — `AppShell`'s own `useMediaQuery(BREAKPOINTS.lg)`,
 * the first statement in its body — is the shell's rail/drawer switch and has
 * nothing to do with this one. Mobile-first: `useMediaQuery` answers false
 * without `matchMedia`, so the fallback is the smaller set.
 *
 * ── UNEXPORTED, DELIBERATELY ────────────────────────────────────────────────
 *
 * `src/test/disclaimerPairing.test.tsx` pins which EXPORTED functions of this
 * file render a `<ScoreCard>`: its `exportedRenderers` helper walks each
 * exported `FunctionDeclaration`, and the test "keeps the disclaimer-less score
 * renderer inside its own module" asserts the result is exactly
 * `['DailyStatusBody', 'ResearchSection']`. This renders none, and exporting it
 * would put a third name in front of a gate that exists to keep the score
 * renderers countable.
 *
 * `maxPoints` is BRAIN's sampling target rather than a cap — the design's own
 * numbers, written as the design writes them.
 */
function Montage() {
  const montage = useMediaQuery(DESKTOP) ? MONTAGE.desktop : MONTAGE.below
  return <BrainScan {...montage} />
}

/**
 * Where the montage goes — on a BrainBit Pro day, and only there.
 *
 * The montage is the Pro's eight sensors, and its screen-reader list names
 * those eight. On a day the backend resolved to any other headband (the
 * four-contact BrainBit) it would draw electrodes that recording never had, so
 * `DailyStatusBody` does not mount the slot at all — canvas, placeholder and
 * sensor list together. A day whose headband is not stated gets none either:
 * every host passes the row's `device_id`, and drawing the Pro's sensors for a
 * device nobody named is the silent Pro fallback in another form.
 *
 * A fixed-size slot, so the hero's two columns have their final geometry
 * whether or not BrainScan's lazy chunk has arrived (`.brain-slot` in
 * `product.css`'s *hero* region sizes it; BrainScan's own placeholder holds the
 * same box inside it).
 *
 * NOT `aria-hidden`: BRAIN's visually-hidden sensor list lives inside
 * `BrainScan`'s root, and an `aria-hidden` ancestor would delete the only path
 * assistive technology has to what the illustration shows (O4, R31). The canvas
 * and the placeholder carry their own `aria-hidden` instead.
 */
function BrainSlot() {
  return (
    <div className="brain-slot">
      <Montage />
    </div>
  )
}

/**
 * The `ui_scores` card set — four rings from the interface vocabulary
 * (Readiness, Recovery balance, Focus stability, Calm), beside the eight
 * measures rather than instead of them.
 *
 * GATED ON `labels`, NOT ON `card_set`. RULINGS R19 deferred this block for two
 * reasons and only one of them has expired: `card_set` ships today, but four
 * cards whose only content is a number is the shape R19 exists to prevent, and
 * that judgement still holds. `labels` (schema 1.4, ask B8) is the reading.
 * So the presence of the READING is the gate — a payload that carries the block
 * without it renders nothing, which is R19 enforced rather than restated.
 *
 * NOT EXPORTED, deliberately. `disclaimerPairing.test.tsx` pins the exported
 * functions in this file that render a `<ScoreCard>` to exactly
 * `['DailyStatusBody', 'ResearchSection']`, so that no screen can mount a score
 * without the disclaimer that must accompany it. A module-local component is
 * inside `DailyStatusBody`'s own subtree and inherits that pairing — the same
 * reasoning the 2026-09-18 correction applied to the unexported hero.
 *
 * SUPPRESSION IS THE PAYLOAD'S. `ui_scores.validity[key].suppressed` has
 * shipped with no consumer since 0.7.1; a suppressed card renders as
 * unavailable through `ScoreCard`'s existing null-value path rather than being
 * dropped, because a card that vanishes says less than one that says why.
 *
 * THE MONTAGE REASON IS THE TOP-LEVEL `validity.unavailable[card]` (eeg-tracking
 * 0.11.0, C8 (e)), with its sentence from `validity.reason_text`: on a headband
 * that lacks the electrodes a card needs, the card shows the payload's sentence
 * instead of a dial, as the measure rows do. It lives there and not in the
 * card's own `ui_scores.validity`, because the backend declares that block
 * `extra="forbid"` (`brainspot/contracts/recording.py`, `UiScoreValidity`)
 * while the top-level `validity` is an open `dict[str, object]`. A token wins
 * over a stray number, the measures' rule (`readDailyStatus`).
 */
const UI_SLOTS = ['readiness', 'recovery', 'focus_stability', 'calm'] as const

function uiHeroCards(block: UiScores | null, validity: DailyValidity | null): ScoreCardData[] | null {
  if (block === null) return null
  const labels = block.labels ?? null
  if (labels === null) return null
  // An absent slot is an incomplete card set. A present null score is a real
  // unavailable card and keeps the four-card layout.
  if (block.scores === null || typeof block.scores !== 'object'
    || !UI_SLOTS.every((key) => Object.hasOwn(block.scores, key))) return null
  if (!UI_SLOTS.every((key) => {
    const display = block.display?.[key]
    return display !== undefined && typeof display.title === 'string' && display.title.trim() !== ''
      && typeof display.description === 'string' && typeof display.evidence_tier === 'string'
  })) return null

  return UI_SLOTS.map((key): ScoreCardData => {
    const suppressed = block.validity[key]?.suppressed === true
    const unavailable = uiCardUnavailable(validity, key)
    const raw = block.scores[key]
    const display = block.display[key]
    return {
      // `ui_scores` keys are its own vocabulary, not SCORE_SLOTS. They never
      // reach a slot-keyed lookup — everything this card needs is in the block,
      // except its montage reason, read by its own key above.
      slot: key as ScoreCardData['slot'],
      value: suppressed || unavailable !== null ? null : (raw ?? null),
      label: labels[key] ?? null,
      labelCode: block.label_codes?.[key] ?? null,
      display: {
        title: display.title,
        description: display.description,
        evidence_tier: display.evidence_tier,
      },
      evidence: null,
      researchOnly: false,
      unavailableReasons: [],
      // The headband cannot produce this card: the payload's reason.
      unavailable,
      // `validity.withheld` names measures, never these four cards: their
      // low-signal-quality suppression also covers `rest_state_drift`, so one
      // token would be wrong half the time (FIRST-DAY-01, not in this ticket).
      withheld: null,
      caveat: null,
    }
  })
}

export function DailyStatusBody({
  status,
  recording,
  afterRows,
}: {
  status: DailyStatus
  recording?: ScoreCardRecording
  afterRows?: ReactNode
}) {
  const recordingCard = status.scores.find((card) => card.slot === RECORDING_SLOT)
  const rings = ringSet(status)
  const heroCards = uiHeroCards(status.research.uiScores, status.validity)
  // The ROW's resolved headband, as the recording card names it. See `BrainSlot`.
  const proDay = recording?.deviceId === PRO_DEVICE_ID
  return (
    <div className="daily subgrid">
      <DailyHeadline status={status} />
      <ConfidenceBanner status={status} />
      <ValidityNote status={status} />
      {/* Every `<ScoreCard>` is written lexically inside THIS function.
          `disclaimerPairing.test.tsx` walks the whole subtree of each exported
          declaration, so a card inside a `.map()` callback is seen and a card
          inside a separate module-local component is not — moving the hero or
          the rows out would silently drop `DailyStatusBody` from the pinned
          pair of score renderers. */}
      <div className="daily__hero subgrid">
        {recordingCard !== undefined && (
          <ScoreCard
            card={recordingCard}
            variant="recording"
            recording={recording}
            media={proDay ? <BrainSlot /> : undefined}
          />
        )}
        <div className={heroCards === null ? 'daily__rings grid-row' : 'daily__rings daily__rings--ui grid-row'}>
          {(heroCards ?? rings).map((card) => (
            <ScoreCard
              key={card.slot}
              card={card}
              variant="ring"
              baselineAt={card.display?.scaleBaseline}
            />
          ))}
        </div>
      </div>
      <section className="card daily__measures" aria-label="Measures in detail">
        <div className="card__head">
          <div className="card__eyebrow">Measures in detail</div>
        </div>
        {rings.map((card) => (
          <ScoreCard
            key={card.slot}
            card={card}
            variant="row"
            baselineAt={card.display?.scaleBaseline}
          />
        ))}
      </section>
      {afterRows}
      <Disclaimer status={status} />
      {/* R73: research notes follow everything the page shows. */}
      <PageEnd>
        <ResearchSection status={status} />
      </PageEnd>
    </div>
  )
}
